import type { AppSettings, DistributionRule, Project, TaxRoundingMode } from '../types';
import { getProjectIds, ruleProjectId } from './settlement';
import { roundCurrencyAmount } from './utils';

const CONSUMPTION_TAX_RATE = 0.10;

export interface TaxSummary {
  netAmount: number;
  consumptionTax: number;
  grossAmount: number;
  withholdingTax: number;
  transferAmount: number;
}

export function calcWithholdingTax(amount: number, roundingMode: TaxRoundingMode): number {
  const raw = amount <= 1_000_000
    ? amount * 0.1021
    : (amount - 1_000_000) * 0.2042 + 102_100;
  return roundCurrencyAmount(raw, roundingMode);
}

export function calcTax(netAmount: number, isIndividual: boolean, settings: AppSettings): TaxSummary {
  const consumptionTax = roundCurrencyAmount(netAmount * CONSUMPTION_TAX_RATE, settings.consumption_tax_rounding);
  const grossAmount = netAmount + consumptionTax;
  const withholdingTax = isIndividual ? calcWithholdingTax(netAmount, settings.withholding_tax_rounding) : 0;
  return { netAmount, consumptionTax, grossAmount, withholdingTax, transferAmount: grossAmount - withholdingTax };
}

export function getDescendantIds(projectId: string, projects: Project[]): string[] {
  return getProjectIds(projectId, projects);
}

export function filterByRuleConditions<T extends { date: string }>(items: T[], rule: DistributionRule): T[] {
  let next = items;
  if (rule.conditions?.date_from) next = next.filter(item => item.date >= rule.conditions!.date_from!);
  if (rule.conditions?.date_to) next = next.filter(item => item.date <= rule.conditions!.date_to!);
  return next;
}

export function projectScopedTransactions<T extends { project_id: string }>(items: T[], projectIds: string[]): T[] {
  if (projectIds.length === 0) return items;
  const ids = new Set(projectIds);
  return items.filter(item => ids.has(item.project_id));
}

export function getRuleProjectId(rule: DistributionRule): string {
  return ruleProjectId(rule) ?? '';
}

export function fallbackProject(projectId: string): Project {
  return {
    id: projectId,
    parent_id: null,
    name: projectId || '未設定プロジェクト',
    description: '',
    code: '',
    created_at: '',
  };
}

export function parentProjectForReport(project: Project | undefined, projects: Project[]): Project | undefined {
  if (!project) return undefined;
  let current = project;
  let parent = current.parent_id ? projects.find(p => p.id === current.parent_id) : undefined;
  const visited = new Set<string>([current.id]);
  while (parent) {
    if (visited.has(parent.id)) return current;
    visited.add(parent.id);
    current = parent;
    parent = current.parent_id ? projects.find(p => p.id === current.parent_id) : undefined;
  }
  return current;
}
